import React, { useRef } from 'react'
import {
  FieldValues, Path, useFormContext, useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { cyrillicRegex } from 'shared/ncts/common/constants'
import CalendarIcon from '../icons/CalendarIcon'
import { DEFAULT_FIELD_RULE, FieldRule } from '../../features/declaration/models/fieldRules'

export type RadioOption = {
  value: string | number
  label: React.ReactNode
  disabled?: boolean
}

export type FormInputProps<T extends FieldValues> = {
  field: Path<T>
  labelKey?: string
  label?: React.ReactNode
  type?: 'text' | 'textarea' | 'number' | 'radio' | 'datetime-local' | 'checkbox' | 'date'
  // placeholder?: string
  // helperText?: React.ReactNode
  // className?: string
  /** Optional RHF validation rules */
  // rules?: RegisterOptions<T, Path<T>>
  /** Optional external/business rule errors keyed by path */
  // businessErrors?: BusinessErrors
  /** Radio-only options */
  radioOptions?: RadioOption[]
  fieldRule?: FieldRule
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'checked'>

function Input<TFieldValues extends FieldValues = FieldValues>({
  field,
  labelKey,
  type,
  radioOptions,
  fieldRule = DEFAULT_FIELD_RULE,
  ...rest
}: FormInputProps<TFieldValues>) {
  const {
    control, register, getFieldState,
  } = useFormContext<TFieldValues>()
  const { t } = useTranslation()
  const dateInputRef = useRef<HTMLInputElement | null>(null)

  const current = String(useWatch({ control, name: field }) ?? '')

  if (type === 'radio') {
    const currentValue = current ?? 'false'
    return (
      <React.Fragment key={`${field}_${type}`}>
        {radioOptions?.map((option) => {
          const rid = `${field}_${type}_${option.value}`

          return (
            <div className="form-check form-check-inline mb-0" key={rid}>
              <input
                id={rid}
                type="radio"
                className={`form-check-input ${getFieldState(field).invalid ? 'is-invalid' : ''
                } ${
                  fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
                }`}
                value={String(option.value)}
                checked={currentValue === String(option.value)}
                {...rest}
                {...register(field)}
              />
              <label className="form-check-label" htmlFor={rid}>
                {t(option.label)}
              </label>
            </div>
          )
        })}

      </React.Fragment>
    )
  }
  if (type === 'textarea') {
    return (
      <React.Fragment key={`${field}_${type}`}>
        <textarea
          id={`${field}_${type}`}
          disabled={fieldRule.requirement === 'forbidden' || rest.disabled}
          className={`form-control ${
            getFieldState(field).invalid ? 'is-invalid' : ''
          } ${
            fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
          }`}
          {...register(field)}
        />
      </React.Fragment>
    )
  }
  if (type === 'datetime-local' || type === 'date') {
    const dateRegistration = register(field, {
      required: fieldRule.requirement === 'required' ? 'Required' : false,
    })
    const isDisabled = fieldRule.requirement === 'forbidden' ? true : rest.disabled
    return (
      <div className="input-group input-group-date" key={`${field}_${type}`}>
        <input
          id={`${field}_${type}`}
          type={type}
          disabled={isDisabled}
          className={`form-control ${getFieldState(field).invalid ? 'is-invalid' : ''
          } ${
            fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
          }`}
          {...rest}
          {...dateRegistration}
          ref={(el) => {
            dateRegistration.ref(el)
            dateInputRef.current = el
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={t(labelKey)}
          disabled={isDisabled}
          className="input-group-text input-group-calendar"
          onClick={() => dateInputRef.current?.showPicker?.()}
        >
          <CalendarIcon />
        </button>
      </div>
    )
  }

  if (type === 'checkbox') {
    return (
      <React.Fragment key={`${field}_${type}`}>
        <div className="form-check form-switch ms-3 me-2">
          <input
            id={`${field}_${type}`}
            type={type}
            disabled={fieldRule.requirement === 'forbidden' || rest.disabled}
            className={`form-check-input ${getFieldState(field).invalid ? 'is-invalid' : ''
            } ${
              fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
            }`}
            {...register(field)}
            {...rest}
          />
          <label className="form-check-label" htmlFor={`${field}_${type}`}>
            {t(labelKey)}
          </label>
        </div>

      </React.Fragment>
    )
  }

  const registerFormField = register(field, {
    onChange: (e: any) => {
      if (e.target.value !== '' && !cyrillicRegex.test(e.target.value)) {
        toast.error(t('messages.forbiddenCharacter', { 0: e.target.value }))
        e.target.value = ''
      }
    },
    required: fieldRule.requirement === 'required' ? 'Required' : false,
    valueAsNumber: type === 'number',
  })

  return (
    <React.Fragment key={`${field}_${type}`}>
      <input
        id={`${field}_${type}`}
        type={type}
        disabled={fieldRule.requirement === 'forbidden' ? true : rest.disabled}
        className={`form-control ${getFieldState(field).invalid ? 'is-invalid' : ''
        } ${
          fieldRule.requirement === 'forbidden' ? 'text-muted cursor--not-allowed' : ''
        }`}
        {...registerFormField}
        {...rest}
      />

    </React.Fragment>
  )
}

Input.defaultProps = {
  type: 'text',
  labelKey: '',
  label: undefined,
  radioOptions: [],
  fieldRule: DEFAULT_FIELD_RULE,
}

export default Input
